import { Link, useFetcher } from '@remix-run/react';
import { analyticsState, AmplitudeEvent, ButtonTag, Operation } from '../state/amplitude';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useDisableBodyScroll } from '../hooks/use-disable-body-scroll';
import { validate as validateEmail } from 'email-validator';
import { EmailInput } from './email-input';
import { ModalCloseButton } from './booking-form/modal-close-button';
import { Modal } from './policy-modal';
import { Tick } from './tick';
import { images } from '../constants/images';
import { Divider } from './divider';
import Sprite from '../sprites/sprite';
import { FACEBOOK, INSTAGRAM, TWITTER, YOUTUBE } from '../constants/socials';
import { FormattedString, useMessages } from '../i18n/useMessages';
import { isInquiryModeEnabled, onOpenInquiry } from '../state/inquiry-modal';

interface FooterProps {
  isHome?: boolean;
}

interface SubscriptionResult {
  success: boolean;
}

export const Footer: React.FC<FooterProps> = ({ isHome = true }) => {
  const fetcher = useFetcher();

  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const onClose = useCallback(() => setIsSubscriptionModalOpen(false), []);
  const onMapModalClose = useCallback(() => setIsMapModalOpen(false), []);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onMapClick = useCallback(() => setIsMapModalOpen(true), []);

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const onChange = useCallback((value: string) => {
    setEmail(value);
    setError('');
  }, []);
  const onSubmit = useCallback(() => {
    analyticsState?.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      buttonTag: ButtonTag.SUBSCRIBE,
    });

    if (!validateEmail(email)) {
      setError('Please enter a valid email.');
      // TODO: sentry?
    } else {
      fetcher.submit(
        { email, intent: 'subscribe' },
        { method: 'post', action: '/?index' },
      );
      setEmail('');
    }
  }, [email, fetcher]);

  useEffect(() => {
    const response = fetcher.data as SubscriptionResult;
    if (response) {
      analyticsState?.amplitude?.track(
        response.success
          ? AmplitudeEvent.OPERATION_SUCCESS
          : AmplitudeEvent.OPERATION_FAIL,
        {
          operationName: Operation.SUBSCRIBE,
        },
      );
      setIsSubscriptionModalOpen(response.success);
    }
  }, [fetcher.data]);

  const isDisabled = email.length === 0;

  const getMessage = useMessages();

  return (
    <>
      <EmailSubscriptionModal isOpen={isSubscriptionModalOpen} onClose={onClose} />
      <MapModal isOpen={isMapModalOpen} onClose={onMapModalClose} />

      {/* Newsletter Subscription Section */}
      {isHome && (
        <section className="bg-gradient-to-b from-yellow-100 to-yellow-200 py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
            <div className="mb-6">
              <h3 className="text-xl lg:text-2xl font-serif font-bold text-gray-900 mb-2">
                Stay informed.
              </h3>
              <p className="text-gray-700 text-sm lg:text-base font-medium">
                We&apos;ll keep you posted on any discounts and sales. No spam!
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              <div className="flex bg-white rounded-xl shadow-lg border border-yellow-300/50 overflow-hidden">
                <EmailInput value={email} onChange={onChange} />
                <button
                  className={clsx(
                    'bg-red-600 hover:bg-red-700 text-white px-8 py-3 font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed',
                    {
                      'cursor-auto bg-gray-300': isDisabled,
                    },
                  )}
                  onClick={onSubmit}
                  disabled={isDisabled}
                >
                  <span className="text-white">→</span>
                </button>
              </div>
              {error && (
                <p className="text-red-600 text-sm mt-2 text-left pl-2">{error}</p>
              )}
            </div>
          </div>
        </section>
      )}

      <footer
        className={clsx(
          'bg-gradient-to-b from-yellow-200 to-yellow-300 border-t border-yellow-400/50',
          {
            'pt-12 lg:pt-16': isHome,
            'pt-16': !isHome,
          },
        )}
        id="footer"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pb-8">
            {/* Location & Contact Info */}
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Address Section */}
                <div>
                  <h4 className="text-lg font-serif font-bold text-gray-900 mb-4">
                    📍 Camp Location
                  </h4>
                  <div className="space-y-2 text-gray-700">
                    <p className="font-semibold text-orange-700 text-sm">
                      Open: 31 Dec 2025 – End of Feb 2026
                    </p>
                    <p className="font-semibold text-gray-900">Kumbh Sector No. 22</p>
                    <p className="text-sm leading-relaxed">
                      {getMessage('camp67-contact.campsite-address')}:<br />
                      9WV9+CJH, Prayagraj, Dewrakh Kachhar,
                      <br />
                      Uttar Pradesh 211019, India
                    </p>
                    <Link
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm mt-2 transition-colors"
                      to="https://maps.app.goo.gl/yMaLiC9mCLvdcMrJ9"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      {getMessage('camp67-contact.map')} →
                    </Link>
                  </div>
                </div>

                {/* Contact Section */}
                <div>
                  <h4 className="text-lg font-serif font-bold text-gray-900 mb-4">
                    📞 Get in Touch
                  </h4>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">For Bookings:</p>
                      <div className="space-y-1 text-gray-700">
                        <p className="inline-flex items-center gap-1">
                          <Sprite name="phone" width="16" height="16" className="w-3.5 h-3.5 fill-current" />
                          <span>/</span>
                          <Sprite name="whatsapp" width="24" height="24" className="w-4 h-4" />
                          <span>+91 84858 35675</span>
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="font-semibold text-gray-900 mb-1">General Queries:</p>
                      <div className="space-y-1 text-gray-700">
                        <p>📞 +91 99100 78872</p>
                        <p>📞 080652 49725</p>
                      </div>
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="flex items-center gap-4 mt-6">
                    <span className="text-sm font-medium text-gray-600">Follow us:</span>
                    <div className="flex gap-3">
                      <a
                        href={FACEBOOK}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-600 hover:text-blue-600 transition-colors"
                      >
                        <Sprite
                          name="facebook"
                          width="64"
                          height="64"
                          className="w-5 h-5"
                        />
                      </a>
                      <a
                        href={INSTAGRAM}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-600 hover:text-pink-600 transition-colors"
                      >
                        <img src="/instagram.png" alt="Instagram" className="w-5 h-5" />
                      </a>
                      <a
                        href={TWITTER}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-600 hover:text-blue-400 transition-colors"
                      >
                        <Sprite
                          name="twitter"
                          width="64"
                          height="64"
                          className="w-5 h-5"
                        />
                      </a>
                      <a
                        href={YOUTUBE}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-600 hover:text-red-600 transition-colors"
                      >
                        <Sprite
                          name="youtube"
                          width="64"
                          height="45"
                          className="w-5 h-5"
                        />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Compact Map */}
              {/* <div className="mt-8">
                <button
                  className="relative group w-full max-w-md bg-transparent border-none p-0"
                  onClick={onMapClick}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onMapClick();
                    }
                  }}
                  aria-label="View Camp67 location map in full screen"
                >
                  <div className="relative overflow-hidden rounded-xl shadow-md">
                    <img
                      src={images['map-to-camp67']}
                      alt="Map to Camp67 location"
                      className="w-full h-32 object-cover transition-all duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-black/20 group-hover:from-black/10 group-hover:to-black/10 transition-all duration-300" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                            />
                          </svg>
                          View Full Map
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div> */}
            </div>

            {/* Navigation & Links */}
            <div className="lg:col-span-4">
              <div className="space-y-8">
                {/* Quick Links */}
                <div>
                  <h4 className="text-lg font-serif font-bold text-gray-900 mb-4">
                    Quick Links
                  </h4>
                  <nav className="grid grid-cols-2 gap-2">
                    <Link
                      prefetch="intent"
                      to="/the-camp"
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-sm"
                    >
                      {getMessage('camp67-navigation.the-camp')}
                    </Link>
                    <Link
                      prefetch="intent"
                      to="/kumbh-2025"
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-sm"
                    >
                      {getMessage('camp67-navigation.kumbh-2025')}
                    </Link>
                    <Link
                      prefetch="intent"
                      to="/packages"
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-sm"
                    >
                      {getMessage('camp67-navigation.packages')}
                    </Link>
                    <Link
                      prefetch="intent"
                      to="/contact-us"
                      className="text-gray-700 hover:text-blue-600 font-medium transition-colors text-sm"
                    >
                      {getMessage('camp67-navigation.contact-us')}
                    </Link>
                  </nav>
                </div>

                {/* CTA */}
                <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-yellow-300/30">
                  <h4 className="font-serif font-bold text-gray-900 mb-1">
                    {getMessage('camp67-header.tagline')}
                  </h4>
                  <p className="text-xs text-gray-600 mb-3">
                    Camp open: 31 Dec 2025 – End of Feb 2026
                  </p>
                  {isInquiryModeEnabled ? (
                    <button
                      onClick={() => onOpenInquiry()}
                      className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      {getMessage('camp67-banner.inquire-now')}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </button>
                  ) : (
                    <Link
                      prefetch="intent"
                      to="/packages/reservation"
                      className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      {getMessage('camp67-banner.book-now')}
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 8l4 4m0 0l-4 4m4-4H3"
                        />
                      </svg>
                    </Link>
                  )}
                </div>

                {/* Non-home Newsletter Subscription */}
                {!isHome && (
                  <div>
                    <h4 className="text-lg font-serif font-bold text-gray-900 mb-4">
                      {getMessage('camp67-footer.subscribe-for-updates')}
                    </h4>
                    <div className="space-y-3">
                      <div className="relative">
                        <EmailInput
                          value={email}
                          onChange={onChange}
                          appearance="subtle"
                        />
                        <button
                          onClick={onSubmit}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-900 transition-colors"
                        >
                          →
                        </button>
                      </div>
                      {error && <p className="text-red-600 text-sm">{error}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="border-t border-yellow-400/30 pt-6 pb-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              {/* Copyright */}
              <div className="text-center md:text-left">
                <p className="text-xs text-gray-600">
                  © 2024 camp67. All rights reserved.
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {getMessage('camp67-footer.a-product-of')}{' '}
                  <a
                    href="https://sukritwellness.com"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sukrit Wellness Tours
                  </a>{' '}
                  Pvt Ltd
                </p>
              </div>

              {/* Policy Links */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
                <Link
                  prefetch="intent"
                  to="/terms-of-service"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {getMessage('camp67-link.terms-of-service')}
                </Link>
                <Link
                  prefetch="intent"
                  to="/privacy-policy"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {getMessage('camp67-link.privacy-policy')}
                </Link>
                <Link
                  prefetch="intent"
                  to="/payment-policy"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {getMessage('camp67-link.payment-policy')}
                </Link>
                <Link
                  prefetch="intent"
                  to="/cancellation-and-refund-policy"
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {getMessage('camp67-link.cancellation-policy')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export const FooterCompact: React.FC = () => {
  const getMessage = useMessages();

  return (
    <footer
      className={clsx(
        'm-auto w-full xl:px-60 lg:px-24 md:px-16 px-8 py-8 text-gray-500 relative border-t-black bg-yellow-500 border-t-2 border-dashed',
      )}
      id="footer"
    >
      <div className="flex flex-row items-center justify-between w-full">
        <span className="hidden uppercase tracking-wider gap-2 md:block text-sm">
          {getMessage('camp67-header.tagline')}{' '}
          {isInquiryModeEnabled ? (
            <button
              onClick={() => onOpenInquiry()}
              className="text-blue-500 underline font-bold mb-3 sm:mb-0"
            >
              {getMessage('camp67-banner.inquire-now')} &rarr;
            </button>
          ) : (
            <Link
              prefetch="intent"
              to="/packages/reservation"
              className="text-blue-500 underline font-bold mb-3 sm:mb-0"
            >
              {getMessage('camp67-banner.book-now')} &rarr;
            </Link>
          )}
        </span>
      </div>
      <p className="text-sm">{getMessage('camp67-contact.helpdesk')}: 080652 49725</p>
      <p className="text-sm">
        {getMessage('camp67-contact.whatsapp-only')}: +91 84858 35675
      </p>
      <p className="text-sm">
        {getMessage('camp67-contact.outside-india')}: +91 80652 49725
      </p>
      <Divider className="border border-[#000]/10 my-6" />
      <div className="flex flex-col xs:flex-row xs:justify-between mt-2">
        <small className="block text-xs font-sans font-light">
          © All Rights Reserved 2024, camp67
        </small>
        <small className="block text-xs font-sans font-light">
          {getMessage('camp67-footer.a-product-of')}{' '}
          <a href="https://sukritwellness.com" className="text-blue-500 underline">
            Sukrit Wellness Tours
          </a>{' '}
          Pvt Ltd
        </small>
      </div>
    </footer>
  );
};

interface MapModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

const MapModal: React.FC<MapModalProps> = ({ isOpen, onClose }) => {
  useDisableBodyScroll(isOpen);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Map view"
    >
      {/* Overlay */}
      <button
        className="absolute inset-0 bg-black/90 backdrop-blur-sm cursor-default"
        onClick={onClose}
        aria-label="Close modal"
        tabIndex={-1}
      />

      <div className="relative w-full h-full p-4 flex items-center justify-center pointer-events-none">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white p-3 rounded-full transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50 pointer-events-auto"
          aria-label="Close map"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* ESC key hint */}
        <div
          className="absolute top-4 left-4 z-10 bg-white/10 backdrop-blur-sm text-white px-3 py-2 rounded-full text-sm pointer-events-none"
          role="status"
          aria-live="polite"
        >
          Press ESC to close
        </div>

        {/* Map image */}
        <div className="max-w-6xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl pointer-events-auto">
          <img
            src={images['map-to-camp67']}
            alt="Detailed map to Camp67 location showing Sector 22, Prayagraj"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Instructions */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
          <div
            className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm text-center"
            role="status"
          >
            Click outside the map or press ESC to close
          </div>
        </div>
      </div>
    </div>
  );
};

interface EmailSubscriptionModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
}

const EmailSubscriptionModal: React.FC<EmailSubscriptionModalProps> = ({
  isOpen,
  onClose,
}) => {
  useDisableBodyScroll(isOpen);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-11/12 md:w-8/12 bg-white pb-10 flex flex-col items-center justify-center h-4/6 rounded-md relative">
        <ModalCloseButton onClose={onClose} />
        <Tick />
        <h2 className="text-4xl font-serif text-red-200 font-semibold text-center w-fit">
          <FormattedString message="camp67-footer.subscribe-success" />
        </h2>
        <p className="mt-4 text-center w-4/6">
          <FormattedString message="camp67-footer.subscribe-success-message" />
        </p>
        <p className="mt-4 text-center w-fit">
          <FormattedString message="camp67-footer.subscribe-opt-out" />
        </p>
      </div>
    </Modal>
  );
};
